import { useState, useRef } from 'react'
import { useApp } from '@/context/AppContext'
import AppHeader from '@/components/layout/AppHeader'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'
import { printCommsLog } from '@/utils/printUtils'
import { apiJson } from '@/utils/auth'

const COMM_TYPES = [
  { value: 'text',    label: '📱 Text message' },
  { value: 'email',   label: '📧 Email' },
  { value: 'verbal',  label: '🗣️ Verbal / in-person' },
  { value: 'app',     label: '💬 Co-parenting app' },
  { value: 'voicemail', label: '📞 Voicemail' },
  { value: 'other',   label: '📎 Other' },
]

const TONE_TAGS = [
  { value: 'neutral',    label: '😐 Neutral',     color: 'bg-slate-100 text-slate-600' },
  { value: 'cooperative', label: '🤝 Cooperative', color: 'bg-green-100 text-green-700' },
  { value: 'hostile',    label: '⚠️ Hostile',      color: 'bg-red-100 text-red-700' },
  { value: 'concerning', label: '🚩 Concerning',   color: 'bg-orange-100 text-orange-700' },
]

const EMPTY_FORM = { date: new Date().toISOString().split('T')[0], time: '', type: '', summary: '', tone: 'neutral', witnesses: '', followup: false, attachments: [] }

const TONE_META = {
  neutral:     { label: '😐 Neutral',     bg: 'bg-slate-100',  text: 'text-slate-700',  border: 'border-slate-300'  },
  cooperative: { label: '🤝 Cooperative', bg: 'bg-green-50',   text: 'text-green-800',  border: 'border-green-300'  },
  hostile:     { label: '⚠️ Hostile',     bg: 'bg-red-50',     text: 'text-red-800',    border: 'border-red-300'    },
  concerning:  { label: '🚩 Concerning',  bg: 'bg-orange-50',  text: 'text-orange-800', border: 'border-orange-300' },
}

export default function CommsLog() {
  const { user, comms, addComm, updateComm } = useApp()
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState(EMPTY_FORM)
  const [formErrors, setFormErrors] = useState({})
  const [filter, setFilter] = useState('all')
  const [toneAnalysis,  setToneAnalysis]  = useState(null)
  const [analyzingTone, setAnalyzingTone] = useState(false)
  const [analyzeError,  setAnalyzeError]  = useState(null)

  const [editEntry,  setEditEntry]  = useState(null)  // entry being edited (null = modal closed)
  const [editForm,   setEditForm]   = useState(EMPTY_FORM)
  const [editSaving, setEditSaving] = useState(false)

  const cameraRef  = useRef(null)
  const galleryRef = useRef(null)
  const fileRef    = useRef(null)

  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setFormErrors(prev => ({ ...prev, [k]: '' })) }

  // Clear stale analysis when the user edits the summary
  const setSummary = (v) => {
    set('summary', v)
    if (toneAnalysis) setToneAnalysis(null)
    if (analyzeError) setAnalyzeError(null)
  }

  async function analyzeTone() {
    if (!form.summary.trim()) return
    setAnalyzingTone(true)
    setToneAnalysis(null)
    setAnalyzeError(null)
    try {
      const result = await apiJson('/api/tone-analyzer', {
        method: 'POST',
        body: JSON.stringify({ message: form.summary }),
      })
      const analysis = result.analysis
      setToneAnalysis(analysis)
      set('tone', analysis.tone) // auto-fill the tone selector
    } catch {
      setAnalyzeError('Tone analysis unavailable. Please select tone manually.')
    } finally {
      setAnalyzingTone(false)
    }
  }

  function useSuggestion(text) {
    set('summary', text)
    setToneAnalysis(prev => ({ ...prev, tone: 'neutral', warning: null, flags: [], suggestion: null }))
    set('tone', 'neutral')
  }

  const save = async () => {
    const errors = {}
    if (!form.date) errors.date = 'Date is required'
    if (!form.type) errors.type = 'Communication type is required'
    if (!form.summary.trim()) errors.summary = 'Summary is required'
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return }
    setFormErrors({})
    await addComm({ ...form })
    setForm(EMPTY_FORM)
    setToneAnalysis(null)
    setAnalyzeError(null)
    setShowForm(false)
  }

  // Read selected files into base64 data URLs for preview + storage
  function handleFiles(fileList) {
    const files = Array.from(fileList)
    files.forEach(file => {
      const reader = new FileReader()
      reader.onload = e => {
        setForm(p => ({
          ...p,
          attachments: [
            ...p.attachments,
            { name: file.name, dataUrl: e.target.result, type: file.type, size: file.size },
          ],
        }))
      }
      reader.readAsDataURL(file)
    })
  }

  function removeAttachment(idx) {
    setForm(p => ({ ...p, attachments: p.attachments.filter((_, i) => i !== idx) }))
  }

  const entries = comms
  const filtered = filter === 'all' ? entries : entries.filter(e => e.tone === filter)
  const toneTag = (tone) => TONE_TAGS.find(t => t.value === tone)

  return (
    <div className="flex flex-col h-full bg-white">
      <AppHeader title="Communication Log" showBack
        right={
          <div className="flex items-center gap-2">
            {entries.length > 0 && (
              <button
                onClick={() => printCommsLog({ userName: user?.name, state: user?.state, entries })}
                className="tap-target w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white/80 hover:bg-white/30"
                title="Export as PDF"
                aria-label="Export communication log as PDF">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
              </button>
            )}
            <button onClick={() => setShowForm(true)}
              className="tap-target w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white/80 hover:bg-white/30"
              aria-label="Add communication entry">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
        }
      />

      <div className="screen no-scrollbar px-4 py-4 flex flex-col gap-4">

        {/* Info banner */}
        {entries.length === 0 && !showForm && (
          <div className="card bg-brand-50 border border-brand-200 text-center py-8 flex flex-col items-center gap-3">
            <span className="text-4xl">💬</span>
            <div>
              <p className="font-bold text-slate-800">Court-Ready Communication Log</p>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                Log every co-parenting communication. Entries are date-stamped, categorized, and exportable for your attorney or court.
              </p>
            </div>
            <Button variant="primary" size="md" onClick={() => setShowForm(true)}>
              Log First Communication
            </Button>
          </div>
        )}

        {/* Add entry form */}
        {showForm && (
          <div className="card border border-brand-200 flex flex-col gap-4 animate-slide-up">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-slate-800">New Entry</h3>
              <button onClick={() => { setShowForm(false); setToneAnalysis(null); setAnalyzeError(null) }} className="text-slate-500 tap-target" aria-label="Close form">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Input label="Date" type="date" value={form.date} onChange={e => set('date', e.target.value)} required />
              <Input label="Time" type="time" value={form.time} onChange={e => set('time', e.target.value)} />
            </div>
            {formErrors.date && <p className="text-red-500 text-sm -mt-2">{formErrors.date}</p>}

            <Input label="Communication type" type="select" value={form.type}
              onChange={e => set('type', e.target.value)}
              placeholder="Select type..." options={COMM_TYPES} required />
            {formErrors.type && <p className="text-red-500 text-sm -mt-2">{formErrors.type}</p>}

            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-2">Summary <span className="text-red-500">*</span></label>
              <textarea value={form.summary} onChange={e => setSummary(e.target.value)}
                placeholder="Paste or type the message you want to check, or summarise what was communicated."
                rows={3} className="input-base resize-none text-sm" />
              {formErrors.summary && <p className="text-red-500 text-sm mt-1">{formErrors.summary}</p>}
              <p className="text-xs text-slate-500 mt-1">Tip: Paste a draft message to check its tone before sending.</p>

              {/* ── Tone Analyzer ─────────────────────────────────────── */}
              {form.summary.trim().length >= 10 && !toneAnalysis && (
                <button
                  type="button"
                  onClick={analyzeTone}
                  disabled={analyzingTone}
                  className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:text-brand-900 disabled:opacity-50 transition-colors"
                >
                  {analyzingTone ? (
                    <>
                      <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Analyzing…
                    </>
                  ) : (
                    <>🔍 Analyze Tone</>
                  )}
                </button>
              )}

              {analyzeError && (
                <p className="mt-2 text-xs text-red-600">{analyzeError}</p>
              )}

              {/* Analysis result panel */}
              {toneAnalysis && (() => {
                const meta = TONE_META[toneAnalysis.tone] || TONE_META.neutral
                const pct  = Math.round((toneAnalysis.confidence ?? 0) * 100)
                const isBad = toneAnalysis.tone === 'hostile' || toneAnalysis.tone === 'concerning'
                return (
                  <div className={`mt-3 rounded-xl border-2 p-3 ${meta.bg} ${meta.border}`}>
                    {/* Header row */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-bold ${meta.text}`}>{meta.label}</span>
                        <span className="text-xs text-slate-500">{pct}% confidence</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setToneAnalysis(null)}
                        className="text-slate-400 hover:text-slate-600"
                        aria-label="Dismiss"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    {/* Flagged phrases */}
                    {toneAnalysis.flags?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {toneAnalysis.flags.map((f, i) => (
                          <span key={i} className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">
                            "{f}"
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Warning */}
                    {toneAnalysis.warning && (
                      <p className={`text-xs leading-relaxed mb-2 ${meta.text}`}>{toneAnalysis.warning}</p>
                    )}

                    {/* Suggested rewrite */}
                    {isBad && toneAnalysis.suggestion && (
                      <div className="mt-2 bg-white rounded-lg border border-slate-200 p-3">
                        <p className="text-xs font-semibold text-slate-600 mb-1.5">Suggested rewrite:</p>
                        <p className="text-sm text-slate-700 leading-relaxed italic">"{toneAnalysis.suggestion}"</p>
                        <button
                          type="button"
                          onClick={() => useSuggestion(toneAnalysis.suggestion)}
                          className="mt-2.5 text-xs font-semibold text-brand-700 hover:text-brand-900 transition-colors"
                        >
                          Use this rewrite →
                        </button>
                      </div>
                    )}

                    {!isBad && (
                      <p className={`text-xs ${meta.text}`}>This message looks court-friendly. Good to log.</p>
                    )}
                  </div>
                )
              })()}
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-2">Tone</label>
              <div className="flex flex-wrap gap-2">
                {TONE_TAGS.map(t => (
                  <button key={t.value} onClick={() => set('tone', t.value)}
                    className={`text-xs px-3 py-1.5 rounded-full font-medium border-2 transition-all ${
                      form.tone === t.value ? t.color + ' border-current' : 'bg-white text-slate-700 border-slate-300'}`}>
                    {t.label}
                  </button>
                ))}
              </div>
              {toneAnalysis && (
                <p className="text-xs text-slate-500 mt-1">Tone auto-filled from analysis — tap to override.</p>
              )}
            </div>

            <Input label="Witnesses (optional)" type="text" value={form.witnesses}
              onChange={e => set('witnesses', e.target.value)}
              placeholder="e.g. Child's teacher was present" />

            {/* ── Attachments ───────────────────────────────────────────── */}
            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-2">
                Attachments <span className="text-slate-400 font-normal">(optional)</span>
              </label>

              {/* Hidden file inputs */}
              <input ref={cameraRef}  type="file" accept="image/*" capture="environment"
                className="hidden" onChange={e => { handleFiles(e.target.files); e.target.value = '' }} />
              <input ref={galleryRef} type="file" accept="image/*,video/*" multiple
                className="hidden" onChange={e => { handleFiles(e.target.files); e.target.value = '' }} />
              <input ref={fileRef}    type="file" accept="image/*,application/pdf,.pdf,.doc,.docx,.txt" multiple
                className="hidden" onChange={e => { handleFiles(e.target.files); e.target.value = '' }} />

              {/* Picker buttons */}
              <div className="grid grid-cols-3 gap-2">
                <button type="button" onClick={() => cameraRef.current?.click()}
                  className="flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 text-slate-600 active:bg-slate-100 transition-colors">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-6 h-6">
                    <path strokeLinecap="round" strokeLinejoin="round"
                      d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <circle cx="12" cy="13" r="3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span className="text-xs font-semibold">Camera</span>
                </button>

                <button type="button" onClick={() => galleryRef.current?.click()}
                  className="flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 text-slate-600 active:bg-slate-100 transition-colors">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-6 h-6">
                    <rect x="3" y="3" width="18" height="18" rx="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 15l5-5 4 4 3-3 6 6" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                  </svg>
                  <span className="text-xs font-semibold">Photos</span>
                </button>

                <button type="button" onClick={() => fileRef.current?.click()}
                  className="flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 text-slate-600 active:bg-slate-100 transition-colors">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-6 h-6">
                    <path strokeLinecap="round" strokeLinejoin="round"
                      d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                  <span className="text-xs font-semibold">File</span>
                </button>
              </div>

              {/* Preview grid */}
              {form.attachments.length > 0 && (
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {form.attachments.map((att, idx) => (
                    <div key={idx} className="relative group">
                      {att.type.startsWith('image/') ? (
                        <img src={att.dataUrl} alt={att.name}
                          className="w-full h-20 object-cover rounded-xl border border-slate-200" />
                      ) : (
                        <div className="w-full h-20 rounded-xl border border-slate-200 bg-slate-100 flex flex-col items-center justify-center gap-1 px-2">
                          <span className="text-xl">📄</span>
                          <span className="text-[9px] text-slate-500 text-center truncate w-full">{att.name}</span>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => removeAttachment(idx)}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 rounded-full text-white flex items-center justify-center shadow-md"
                        aria-label="Remove attachment"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} className="w-3 h-3">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-2 items-center">
              <Button variant="ghost" size="md" className="flex-1" onClick={() => { setShowForm(false); setToneAnalysis(null); setAnalyzeError(null) }}>Cancel</Button>
              <Button variant="primary" size="md" className="flex-1" onClick={save}>
                Save Entry
              </Button>
            </div>
          </div>
        )}

        {/* Filter bar */}
        {entries.length > 0 && (
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {[['all','All'], ...TONE_TAGS.map(t => [t.value, t.label])].map(([v, l]) => (
              <button key={v} onClick={() => setFilter(v)}
                className={`flex-shrink-0 text-xs px-3 py-1.5 rounded-full font-medium transition-all ${filter === v ? 'bg-brand-700 text-white' : 'bg-slate-100 text-slate-600'}`}>
                {l}
              </button>
            ))}
          </div>
        )}

        {/* Entries list */}
        {filtered.map(entry => {
          const tag = toneTag(entry.tone)
          return (
            <div key={entry.id} className="card flex flex-col gap-2">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm text-slate-500">{entry.date}{entry.time ? ` at ${entry.time}` : ''}</p>
                  <p className="text-sm font-semibold text-slate-600 mt-0.5">
                    {COMM_TYPES.find(c => c.value === entry.type)?.label || entry.type}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${tag?.color}`}>
                    {tag?.label}
                  </span>
                  <button
                    onClick={() => {
                      setEditEntry(entry)
                      setEditForm({
                        date: entry.date || '',
                        time: entry.time || '',
                        type: entry.type || '',
                        summary: entry.summary || '',
                        tone: entry.tone || 'neutral',
                        witnesses: entry.witnesses || '',
                        followup: !!entry.followup,
                        attachments: entry.attachments || [],
                      })
                    }}
                    className="tap-target w-7 h-7 rounded-full flex items-center justify-center text-slate-400 hover:text-brand-700 hover:bg-brand-50 transition-colors"
                    aria-label="Edit entry">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                </div>
              </div>
              <p className="text-base text-slate-700 leading-relaxed">{entry.summary}</p>
              {entry.witnesses && (
                <p className="text-sm text-slate-500">👥 Witnesses: {entry.witnesses}</p>
              )}
              {entry.attachments?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-1">
                  {entry.attachments.map((att, i) =>
                    att.type?.startsWith('image/') ? (
                      <img key={i} src={att.dataUrl} alt={att.name}
                        className="w-16 h-16 object-cover rounded-lg border border-slate-200 cursor-pointer"
                        onClick={() => window.open(att.dataUrl, '_blank')} />
                    ) : (
                      <a key={i} href={att.dataUrl} download={att.name}
                        className="flex items-center gap-1.5 bg-slate-100 rounded-lg px-2.5 py-1.5 text-xs text-slate-600 font-medium">
                        <span>📄</span> {att.name}
                      </a>
                    )
                  )}
                </div>
              )}
            </div>
          )
        })}

        {entries.length > 0 && (
          <>
            <div className="flex gap-2">
              <Button variant="secondary" size="md" className="flex-1" onClick={() => setShowForm(true)}>
                + Add Entry
              </Button>
              <Button variant="ghost" size="md" className="flex-1"
                onClick={() => printCommsLog({ userName: user?.name, state: user?.state, entries })}>
                🖨️ Export PDF
              </Button>
            </div>
            <div className="disclaimer">
              ⚠️ This log is for your personal records. Share with your attorney before submitting to court. Keep entries factual and objective — emotional language can undermine credibility.
            </div>
          </>
        )}
      </div>

      {/* ── Edit Entry Modal ─────────────────────────────────────────── */}
      {editEntry && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40"
          onClick={(e) => { if (e.target === e.currentTarget) setEditEntry(null) }}>
          <div className="w-full max-w-lg bg-white rounded-t-2xl shadow-2xl flex flex-col gap-4 p-5 pb-8 animate-slide-up max-h-[90vh] overflow-y-auto no-scrollbar">

            <div className="flex items-center justify-between">
              <h3 className="font-bold text-slate-800">Edit Entry</h3>
              <button onClick={() => setEditEntry(null)} className="text-slate-500 tap-target" aria-label="Close">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Input label="Date" type="date" value={editForm.date} onChange={e => setEditForm(p => ({ ...p, date: e.target.value }))} required />
              <Input label="Time" type="time" value={editForm.time} onChange={e => setEditForm(p => ({ ...p, time: e.target.value }))} />
            </div>

            <Input label="Communication type" type="select" value={editForm.type}
              onChange={e => setEditForm(p => ({ ...p, type: e.target.value }))}
              placeholder="Select type..." options={COMM_TYPES} required />

            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-2">Summary <span className="text-red-500">*</span></label>
              <textarea value={editForm.summary} onChange={e => setEditForm(p => ({ ...p, summary: e.target.value }))}
                rows={3} className="input-base resize-none text-sm" />
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-2">Tone</label>
              <div className="flex flex-wrap gap-2">
                {TONE_TAGS.map(t => (
                  <button key={t.value} onClick={() => setEditForm(p => ({ ...p, tone: t.value }))}
                    className={`text-xs px-3 py-1.5 rounded-full font-medium border-2 transition-all ${
                      editForm.tone === t.value ? t.color + ' border-current' : 'bg-white text-slate-700 border-slate-300'}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <Input label="Witnesses (optional)" type="text" value={editForm.witnesses}
              onChange={e => setEditForm(p => ({ ...p, witnesses: e.target.value }))}
              placeholder="e.g. Child's teacher was present" />

            <div className="flex gap-2 items-center">
              <Button variant="ghost" size="md" className="flex-1" onClick={() => setEditEntry(null)}>Cancel</Button>
              <Button variant="primary" size="md" className="flex-1"
                disabled={!editForm.date || !editForm.type || !editForm.summary || editSaving}
                onClick={async () => {
                  setEditSaving(true)
                  try {
                    await updateComm(editEntry.id, {
                      date: editForm.date,
                      time: editForm.time || null,
                      type: editForm.type,
                      summary: editForm.summary,
                      tone: editForm.tone,
                      witnesses: editForm.witnesses || null,
                      followup: editForm.followup,
                    })
                    setEditEntry(null)
                  } finally {
                    setEditSaving(false)
                  }
                }}>
                {editSaving ? 'Saving…' : 'Save Changes'}
              </Button>
            </div>

          </div>
        </div>
      )}
    </div>
  )
}
